/**
 * Client for POST /api/cognitory/v2/library/documents (PDF upload).
 */

import {
  COGNITORY_V2_LIBRARY_DOCUMENTS_PATH,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";

export type GovernedLibraryUploadSuccess = {
  kind: "uploaded";
  requestId: string;
  documentId: string;
  documentVersionId: string;
  title: string;
  filename: string;
  versionStatus: string;
};

export type CognitoryLibraryUploadClientResult =
  | GovernedLibraryUploadSuccess
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "invalid_request"
        | "pdf_rejected"
        | "document_collision"
        | "temporary_failure";
      requestId: string | null;
    };

export async function uploadGovernedLibraryPdf(input: {
  cognitoryId: string;
  file: File;
  title?: string;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<CognitoryLibraryUploadClientResult> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const token =
    input.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", requestId: null };
  }

  const base = (input.apiBaseUrl ?? resolveCognitoryApiBaseUrl()).replace(
    /\/+$/,
    "",
  );
  const url = new URL(`${base}${COGNITORY_V2_LIBRARY_DOCUMENTS_PATH}`);
  url.searchParams.set("cognitoryId", input.cognitoryId);

  const form = new FormData();
  form.append("file", input.file, input.file.name);
  if (input.title) {
    form.append("title", input.title);
  }

  const response = await fetchImpl(url.toString(), {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });

  const requestIdHeader = response.headers.get("X-Cognitory-Request-Id");
  let body: Record<string, unknown> = {};
  try {
    body = (await response.json()) as Record<string, unknown>;
  } catch {
    body = {};
  }

  const requestId =
    typeof body.requestId === "string"
      ? body.requestId
      : (requestIdHeader ?? null);

  const decision = typeof body.decision === "string" ? body.decision : "";

  if (response.status === 201 && decision === "library_document_uploaded") {
    return {
      kind: "uploaded",
      requestId: requestId ?? "",
      documentId: String(body.documentId ?? ""),
      documentVersionId: String(body.documentVersionId ?? ""),
      title: String(body.title ?? ""),
      filename: String(body.filename ?? ""),
      versionStatus: String(body.versionStatus ?? ""),
    };
  }

  if (decision === "unauthenticated") {
    return { kind: "unauthenticated", requestId };
  }
  if (decision === "forbidden") {
    return { kind: "forbidden", requestId };
  }
  if (decision === "invalid_request") {
    return { kind: "invalid_request", requestId };
  }
  if (decision === "pdf_rejected") {
    return { kind: "pdf_rejected", requestId };
  }
  if (decision === "document_collision") {
    return { kind: "document_collision", requestId };
  }
  if (decision === "temporary_failure") {
    return { kind: "temporary_failure", requestId };
  }

  return { kind: "temporary_failure", requestId };
}
